// Modal con las piezas de una tienda: «En tienda» o «Vendidas», resumen, aviso de apartados y buscador.
import { useState } from 'react';
import type { InventoryPieceEntry } from '@/api/services/retail-catalog/backoffice';
import { Modal, Tabs } from '@/kit';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    filterPieces,
    pieceCountLabel,
    pieceInSegment,
    piecesSummary,
    pieceStatusLabel,
    reservedWithoutPieceNotice,
    type PieceSegment,
    type StoreDistributionRow,
} from '../../lib/inventoryDetail';
import { CopyText } from './CopyText';

const FIELD =
    'h-[26px] rounded border border-border bg-card px-2 text-[11px] text-foreground focus:border-ring focus:outline-none';

const COLUMNS: DataTableColumn<InventoryPieceEntry>[] = [
    {
        key: 'identifier',
        label: 'Identificador',
        width: '65%',
        cell: (piece) => <CopyText value={piece.identifier} label="identificador" />,
    },
    {
        key: 'status',
        label: 'Estado',
        width: '35%',
        cell: (piece) => pieceStatusLabel(piece),
    },
];

function PiecesContent({ store }: { store: StoreDistributionRow }) {
    const [query, setQuery] = useState('');
    const [segment, setSegment] = useState<PieceSegment>('store');
    const count = (s: PieceSegment) => store.pieces.filter((p) => pieceInSegment(p, s)).length;
    const total = count(segment);
    const shown = filterPieces(store.pieces, query, segment);
    const notice = segment === 'store' ? reservedWithoutPieceNotice(store) : null;
    return (
        <div className="flex max-h-[60vh] flex-col gap-2">
            <p className="text-[11.5px] font-semibold text-foreground">{piecesSummary(store)}</p>
            <Tabs
                wrap
                tabs={[
                    { key: 'store', label: `En tienda ${String(count('store'))}` },
                    { key: 'sold', label: `Vendidas ${String(count('sold'))}` },
                ]}
                activeTab={segment}
                onChange={(key) => {
                    setSegment(key as PieceSegment);
                }}
            />
            {notice ? (
                <p role="status" className="text-[11px] font-semibold text-warning">
                    {notice}
                </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="search"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                    }}
                    placeholder="Busca por IMEI o serie"
                    aria-label="Buscar IMEI o serie"
                    className={`${FIELD} min-w-0 flex-1`}
                />
                <span
                    aria-live="polite"
                    className="text-[10.5px] font-semibold text-muted-foreground"
                >
                    {pieceCountLabel(shown.length, total)}
                </span>
            </div>
            <DataTable
                caption={`Piezas de ${store.name}`}
                columns={COLUMNS}
                rows={shown}
                rowKey={(piece) => piece.identifier}
                density="compact"
                className="rounded border border-border"
                empty={
                    total === 0
                        ? segment === 'store'
                            ? 'Esta tienda no tiene piezas de este SKU.'
                            : 'Esta tienda no ha vendido piezas de este SKU.'
                        : 'Ninguna pieza coincide con la búsqueda.'
                }
            />
        </div>
    );
}

export function StorePiecesModal({
    store,
    onClose,
}: {
    /** La tienda elegida; `null` con el modal cerrado. */
    store: StoreDistributionRow | null;
    onClose: () => void;
}) {
    return (
        <Modal
            isOpen={store !== null}
            onClose={onClose}
            title={store ? `Piezas · ${store.name} (${store.code})` : undefined}
        >
            {store ? <PiecesContent key={store.storeId} store={store} /> : null}
        </Modal>
    );
}
